// ============================================================================
// SYNAPS — sample-based neuron/receptor generative synth
//
// Inspired by soulwire/NeuroSynth (neuron→receptor distance-gated firing,
// pentatonic scale + octave-per-neuron, screen-position → stereo pan) but
// rebuilt from scratch on native canvas (no PIXI) with a sample-based
// wavetable oscillator: drag a sample from the bucket onto a neuron and it
// becomes that neuron's wavetable (one cycle extracted via autocorrelation,
// rendered as a PeriodicWave). Falls back to a built-in waveform per neuron
// when no sample is assigned.
//
// Reuses the shared FX rack (afxrack) + LFO system from amenpi/kaeps/granulr.
// ============================================================================

const { qs, qsa, build, alertmessage } = require('../../libs/qs')
const midi = require('../common/midi')

// ---- Theme ----
let currentTheme
try { const s = localStorage.getItem('currentTheme'); if (s) currentTheme = JSON.parse(s) } catch (e) {}
if (!currentTheme) currentTheme = window.Theme
const themeColors = Object.values(currentTheme.data)
const themeRgb = themeColors.map((el) => ({
    r: parseInt(el.slice(1, 3), 16), g: parseInt(el.slice(3, 5), 16), b: parseInt(el.slice(5, 7), 16)
}))
window.CONTROLZ = {}
window.themeColors = themeColors
window.currentTheme = currentTheme
window.themeRgb = themeRgb
const getThemeColors = () => window.themeColors || themeColors

// ---- LFO bank setup (matches amenpi/kaeps pattern) ----
window.openedbank = 'synaps'
window.lfo = window.lfo || {}
window.lfo['synaps'] = {}
window.lfoz = window.lfo['synaps']
window.lfoindex = 0
window.lfobtns = window.lfobtns || {}
window.lfobtns['synaps'] = []
window.lfolinks = window.lfolinks || {}
window.lfolinks['synaps'] = {}
window.bandlinks = {}
window.macrolinks = {}
window.midilinks = {}

const uuid = require('uuid').v4
const engine = require('../common/audioengine')
const fxs = require('../common/audiofx')
const { faderH } = require('../common/ctrlz.js')
const Recorder = require('../common/Recorder')
const lfo = require('../common/lfo')
const transport = require('../common/transport')
const getamepicadres = require('../common/getamepicadres')

const store = {}

const debounce = (func, wait) => {
    let timeout
    const fn = function executedFunction(...args) {
        const later = () => { clearTimeout(timeout); func(...args) }
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
    }
    fn.cancel = () => { clearTimeout(timeout) }
    return fn
}

// ============================================================================
// Music theory helpers
// ============================================================================

// Pentatonic scale (no dissonance) — notes per receptor
const NOTES = ['C', 'D', 'E', 'G', 'A']
const OCTAVES = [3, 4, 5, 6, 7]
const NOTE_SEMITONES = { C: 0, 'C#': 1, D: 2, 'D#': 3, E: 4, F: 5, 'F#': 6, G: 7, 'G#': 8, A: 9, 'A#': 10, B: 11 }

function noteFreq(note, octave) {
    const semitone = NOTE_SEMITONES[note] + (octave - 4) * 12
    // MIDI 69 = A4 = 440Hz
    return 440 * Math.pow(2, (semitone - 69) / 12)
}

// ============================================================================
// Wavetable extraction from a sample buffer
// ============================================================================
// One cycle is extracted via autocorrelation (find the fundamental period),
// then rendered into a PeriodicWave via a direct DFT of that cycle. The
// resulting OscillatorNode plays at the receptor's note frequency with the
// timbre of the dragged sample — a true single-cycle wavetable.

function findPeriod(data, sampleRate, minFreq, maxFreq) {
    const minLag = Math.floor(sampleRate / maxFreq)
    const maxLag = Math.min(Math.floor(sampleRate / minFreq), data.length - 1)
    if (maxLag <= minLag) return minLag
    // Normalized autocorrelation over a useful window
    const winLen = Math.min(data.length - maxLag, maxLag * 4)
    if (winLen <= 0) return minLag
    let bestLag = minLag
    let bestCorr = -Infinity
    for (let lag = minLag; lag <= maxLag; lag++) {
        let corr = 0
        for (let i = 0; i < winLen; i++) {
            corr += data[i] * data[i + lag]
        }
        // Normalize by lag to avoid bias toward longer lags
        corr /= (winLen + lag)
        if (corr > bestCorr) { bestCorr = corr; bestLag = lag }
    }
    return bestLag
}

function extractCycle(data, period) {
    // Find a zero crossing near the start for a clean cycle boundary
    let start = 0
    for (let i = 0; i < data.length - period - 1; i++) {
        if (data[i] <= 0 && data[i + 1] > 0) { start = i; break }
    }
    const cycle = new Float32Array(period)
    for (let i = 0; i < period; i++) cycle[i] = data[start + i] || 0
    return cycle
}

// ============================================================================
// Wavetable extraction — harmonic coefficients from sample segments
// ============================================================================
// Instead of building a single PeriodicWave per sample, we extract the
// harmonic coefficients (real/imag DFT arrays) from MULTIPLE segments of
// the sample and average them. These coefficients can then be MIXED across
// samples with different weights per neuron, so the more samples in the
// stack, the richer every neuron's composite waveform becomes.

function cycleToHarmonics(cycle) {
    const N = cycle.length
    const harmonics = Math.min(Math.floor(N / 2), 512)
    const real = new Float32Array(harmonics + 1)
    const imag = new Float32Array(harmonics + 1)
    for (let k = 0; k <= harmonics; k++) {
        let re = 0, im = 0
        const w = (2 * Math.PI * k) / N
        for (let n = 0; n < N; n++) {
            re += cycle[n] * Math.cos(w * n)
            im += cycle[n] * Math.sin(w * n)
        }
        real[k] = (2 * re) / N
        imag[k] = (2 * im) / N
    }
    real[0] = 0 // remove DC offset
    return { real, imag, harmonics }
}

// Average multiple harmonics objects into one
function averageHarmonics(list) {
    if (list.length === 0) return null
    const maxHarm = Math.max(...list.map(h => h.harmonics))
    const real = new Float32Array(maxHarm + 1)
    const imag = new Float32Array(maxHarm + 1)
    for (const h of list) {
        for (let k = 0; k <= h.harmonics; k++) {
            real[k] += h.real[k]
            imag[k] += h.imag[k]
        }
    }
    const n = list.length
    for (let k = 0; k <= maxHarm; k++) {
        real[k] /= n
        imag[k] /= n
    }
    real[0] = 0
    return { real, imag, harmonics: maxHarm }
}

// Extract harmonics from a single window of audio data
function windowToHarmonics(data, start, winSize, sampleRate) {
    const window = new Float32Array(winSize)
    for (let i = 0; i < winSize; i++) window[i] = data[start + i] || 0
    let period = findPeriod(window, sampleRate, 50, 2000)
    if (period < 8 || period >= winSize) return null
    // Double the period — extract 2 cycles as one wavetable period.
    // This shifts the harmonic content so the oscillator plays an octave higher,
    // fixing the "too low pitched" issue with sample-based wavetables.
    period = period * 2
    if (period >= winSize) period = Math.floor(period / 2) // fall back if not enough data
    const cycle = extractCycle(window, period)
    return cycleToHarmonics(cycle)
}

// Extract harmonics from an AudioBuffer by sampling multiple segments.
// Returns { real, imag, harmonics } or null on failure.
function bufferToHarmonics(buffer) {
    try {
        if (!buffer || buffer.length < 64) return null
        const data = buffer.getChannelData(0)
        const winSize = Math.min(data.length, 4096)
        if (data.length < winSize) {
            // Short sample — just use what we have
            return windowToHarmonics(data, 0, Math.min(data.length, 2048), buffer.sampleRate)
        }

        // Extract from multiple segments: find the loudest windows
        const numSegments = 4
        const step = Math.floor((data.length - winSize) / numSegments)
        const candidates = []

        for (let seg = 0; seg < numSegments; seg++) {
            const start = seg * step
            // Quick energy measure
            let e = 0
            for (let i = 0; i < winSize; i += 8) e += data[start + i] * data[start + i]
            candidates.push({ start, energy: e })
        }

        // Sort by energy, take top 3
        candidates.sort((a, b) => b.energy - a.energy)
        const top = candidates.slice(0, 3)

        const harmonicsList = []
        for (const c of top) {
            const h = windowToHarmonics(data, c.start, winSize, buffer.sampleRate)
            if (h) harmonicsList.push(h)
        }

        if (harmonicsList.length === 0) return null
        return averageHarmonics(harmonicsList)
    } catch (e) {
        console.error('[synaps] harmonics extraction failed', e)
        return null
    }
}

// Mix multiple samples' harmonics with per-neuron weights into a PeriodicWave
function mixHarmonicsToWave(ctx, harmonicsList, weights) {
    const active = []
    for (let i = 0; i < harmonicsList.length; i++) {
        if (weights[i] > 0.001 && harmonicsList[i]) active.push({ h: harmonicsList[i], w: weights[i] })
    }
    if (active.length === 0) return null

    const maxHarm = Math.max(...active.map(a => a.h.harmonics))
    const real = new Float32Array(maxHarm + 1)
    const imag = new Float32Array(maxHarm + 1)
    let totalWeight = 0
    for (const a of active) {
        totalWeight += a.w
        for (let k = 0; k <= a.h.harmonics; k++) {
            real[k] += a.h.real[k] * a.w
            imag[k] += a.h.imag[k] * a.w
        }
    }
    if (totalWeight > 0) {
        for (let k = 0; k <= maxHarm; k++) {
            real[k] /= totalWeight
            imag[k] /= totalWeight
        }
    }
    real[0] = 0
    return ctx.createPeriodicWave(real, imag, { disableNormalization: false })
}

// ============================================================================
// Voice — a single note triggered by a receptor firing
// ============================================================================

function Voice(ctx, freq, periodicWave, oscType, attack, release, panX, detune, output) {
    // No sample wavetable → no sound. The synth is sample-based.
    if (!periodicWave) return
    const now = ctx.currentTime
    const osc = ctx.createOscillator()
    osc.setPeriodicWave(periodicWave)
    osc.frequency.value = freq
    if (detune > 0) osc.detune.value = (Math.random() * 2 - 1) * detune

    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0, now)
    gain.gain.linearRampToValueAtTime(0.5, now + attack)
    gain.gain.linearRampToValueAtTime(0, now + attack + release)

    // Stereo panner (modern API) — receptor screen position
    const panner = ctx.createStereoPanner()
    panner.pan.value = Math.max(-1, Math.min(1, panX))

    osc.connect(gain)
    gain.connect(panner)
    panner.connect(output)

    osc.start(now)
    osc.stop(now + attack + release + 0.05)

    // GC
    const totalMs = (attack + release + 0.1) * 1000
    setTimeout(() => {
        try { osc.disconnect() } catch (e) {}
        try { gain.disconnect() } catch (e) {}
        try { panner.disconnect() } catch (e) {}
    }, totalMs + 200)
}

// ============================================================================
// Node model — neurons (octave) and receptors (note)
// ============================================================================

function makeNode(x, y, color, label, radius) {
    return {
        id: uuid(),
        x, y,
        vx: 0, vy: 0,
        wanderTheta: Math.random() * Math.PI * 2,
        wanderSpeed: 0.012 + Math.random() * 0.038,
        wanderStep: 0.08 + Math.random() * 0.17,
        color,
        label,
        radius: radius || 14,
        pulse: 0, // 0..1 visual activation flash
    }
}

// ============================================================================
// Init
// ============================================================================

async function init() {

    await midi()
    window.midilearn = false
    window.midiLearnElement = null

    store.fxrack = []
    store.neurons = []      // {node, octave, periodicWave, oscType, sampleIdx, weights}
    store.receptors = []    // {node, note}
    store.synapses = []     // {neuron, receptor, strength, connected, pulseT}
    store.paused = false
    store.sequencerQueue = []
    store.sequencerCountdown = 0
    store.lastClock = 0
    store.sampleStack = []  // {id, name, buffer, harmonics, element}

    // ---- Audio context + FX rack ----
    store.audioContext = new AudioContext()
    window.audioContext = store.audioContext
    transport.init(store.audioContext, { bpm: 90 })
    transport._bindExisting(document)

    const arack = await require('../common/afxrack').init(store, null, store.audioContext)
    const out = store.audioContext.createGain()
    arack.output.connect(out)
    out.connect(store.audioContext.destination)

    // ---- faderH controls ----
    const faderIds = ['outvolume', 'range', 'wander', 'attack', 'release', 'detune', 'neuroncount', 'blend']
    faderIds.forEach(id => {
        const el = qs('#' + id)
        if (el) { faderH(el); store[id] = el }
    })

    // ---- Canvas refs ----
    const outputCanvas = qs('#vizu')
    const outputCtx = outputCanvas.getContext('2d')
    const fieldCanvas = qs('#field')
    const fieldCtx = fieldCanvas.getContext('2d')
    const fieldHolder = qs('.synaps-field')
    const helpEl = qs('#synapshelp')

    let W = fieldCanvas.width
    let H = fieldCanvas.height

    const resizeCanvases = () => {
        const rect = fieldHolder.getBoundingClientRect()
        W = Math.max(100, rect.width)
        H = Math.max(100, rect.height)
        fieldCanvas.width = W
        fieldCanvas.height = H
        const orect = outputCanvas.getBoundingClientRect()
        outputCanvas.width = Math.max(100, orect.width)
        outputCanvas.height = Math.max(100, orect.height)
        // Clamp all nodes into the new bounds
        const clampNode = (node) => {
            node.x = Math.max(node.radius, Math.min(W - node.radius, node.x))
            node.y = Math.max(node.radius, Math.min(H - node.radius, node.y))
        }
        store.neurons.forEach(n => clampNode(n.node))
        store.receptors.forEach(r => clampNode(r.node))
    }

    // ---- Cadre + spectrogram color scale (matches amenpi/granulr) ----
    let cadres = null
    window.addEventListener('themechange', async () => {
        setTimeout(async () => {
            cadres = await getamepicadres(store)
        }, 100)
    })
    setTimeout(() => window.dispatchEvent(new Event('themechange')), 200)
    if (window.themeColors && window.themeColors.length > 0) {
        cadres = await getamepicadres(store)
    }

    // ---- Live output analyser + spectrogram ----
    const oscillo = store.audioContext.createAnalyser()
    oscillo.fftSize = 2048
    const oscilloBufferLen = oscillo.frequencyBinCount
    const oscilloData = new Uint8Array(oscilloBufferLen)
    const analyser = store.audioContext.createAnalyser()
    analyser.smoothingTimeConstant = 0.3
    analyser.fftSize = 512
    const specCanvas = build('canvas')
    const specCtx = specCanvas.getContext('2d')
    const tempCanvas = build('canvas')
    const tempCtx = tempCanvas.getContext('2d')
    specCanvas.width = tempCanvas.width = 200
    specCanvas.height = tempCanvas.height = 200

    out.connect(oscillo)
    out.connect(analyser)

    // Simple theme-based spectrogram color ramp
    const specColor = (v) => {
        const colors = getThemeColors()
        const t = Math.min(1, v / 255)
        const idx = Math.floor(t * (colors.length - 1))
        return colors[idx] || colors[colors.length - 1] || '#fff'
    }

    // ============================================================================
    // Build the neuron/receptor field
    // ============================================================================

    // Neurons + receptors use theme colors (brighter range: indices 9-15)
    const FALLBACK_OSC_TYPES = ['triangle', 'sine', 'sawtooth', 'square', 'triangle', 'sine', 'sawtooth', 'square']

    const getNeuronColor = (i) => {
        const colors = getThemeColors()
        // Use brighter theme colors (indices 9..15) cycling
        const idx = 9 + (i % 7)
        return colors[idx] || colors[colors.length - 1] || '#888888'
    }

    const getReceptorColor = (i) => {
        const colors = getThemeColors()
        // Use mid-bright theme colors (indices 7..13) cycling, offset from neurons
        const idx = 7 + ((i + 3) % 7)
        return colors[idx] || colors[colors.length - 1] || '#aaaaaa'
    }

    const layoutField = () => {
        const margin = 40
        const cols = Math.ceil(Math.sqrt(store.neurons.length + store.receptors.length))
        const cellW = (W - margin * 2) / cols
        const cellH = (H - margin * 2) / cols
        const cells = []
        for (let y = 0; y < cols; y++) {
            for (let x = 0; x < cols; x++) {
                cells.push({ x: margin + x * cellW + cellW / 2, y: margin + y * cellH + cellH / 2 })
            }
        }
        // Shuffle cells
        for (let i = cells.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[cells[i], cells[j]] = [cells[j], cells[i]]
        }
        let ci = 0
        store.neurons.forEach(n => {
            const c = cells[ci++] || { x: W / 2, y: H / 2 }
            n.node.x = c.x + (Math.random() - 0.5) * cellW * 0.4
            n.node.y = c.y + (Math.random() - 0.5) * cellH * 0.4
        })
        store.receptors.forEach(r => {
            const c = cells[ci++] || { x: W / 2, y: H / 2 }
            r.node.x = c.x + (Math.random() - 0.5) * cellW * 0.4
            r.node.y = c.y + (Math.random() - 0.5) * cellH * 0.4
        })
    }

    const createNeurons = () => {
        const count = parseInt(store.neuroncount ? store.neuroncount.value : 5)
        store.neurons = []
        for (let i = 0; i < count; i++) {
            const octave = OCTAVES[i % OCTAVES.length]
            const color = getNeuronColor(i)
            const node = makeNode(W / 2, H / 2, color, 'O' + octave, 16)
            store.neurons.push({
                node,
                octave,
                periodicWave: null,
                sampleName: null,
                sampleIdx: -1,
                weights: [],
                oscType: FALLBACK_OSC_TYPES[i % FALLBACK_OSC_TYPES.length],
            })
        }
    }

    const createReceptors = () => {
        const count = 12
        store.receptors = []
        for (let i = 0; i < count; i++) {
            const note = NOTES[i % NOTES.length]
            const color = getReceptorColor(i)
            const node = makeNode(W / 2, H / 2, color, note, 10)
            store.receptors.push({ node, note })
        }
    }

    const rebuildSynapses = () => {
        store.synapses = []
        store.neurons.forEach(n => {
            store.receptors.forEach(r => {
                store.synapses.push({ neuron: n, receptor: r, strength: 0, connected: false, pulseT: 0 })
            })
        })
    }

    const randomize = () => {
        createNeurons()
        createReceptors()
        layoutField()
        rebuildSynapses()
        // Auto-assign available stack samples to neurons
        store.neurons.forEach((n, i) => {
            if (store.sampleStack[i]) {
                n.sampleIdx = i
                n.sampleName = store.sampleStack[i].name
            }
        })
        recomputeAllNeuronWaves()
        updateStackHighlights()
        if (helpEl) helpEl.style.display = 'none'
    }

    // ============================================================================
    // Sequencer — quantize receptor activations to tempo
    // ============================================================================

    const getTempo = () => transport.bpm || 90
    const getInterval = () => (60000 / getTempo()) / 4 // 16th notes

    const scheduleNote = (note, octave, panX, periodicWave, oscType) => {
        store.sequencerQueue.push({ note, octave, panX, periodicWave, oscType })
    }

    const tickSequencer = (dt) => {
        store.sequencerCountdown -= dt
        if (store.sequencerCountdown <= 0) {
            store.sequencerCountdown = getInterval()
            if (store.sequencerQueue.length === 0) return
            // Play all queued notes (cap polyphony to avoid overload)
            const max = 8
            const play = store.sequencerQueue.splice(0, max)
            store.sequencerQueue.length = 0 // drop overflow
            const attack = Number(store.attack.value)
            const release = Number(store.release.value)
            const detune = Number(store.detune.value)
            play.forEach(ev => {
                const freq = noteFreq(ev.note, ev.octave)
                Voice(store.audioContext, freq, ev.periodicWave, ev.oscType,
                    attack, release, ev.panX, detune, arack.input)
            })
        }
    }

    // ============================================================================
    // Update + draw loop
    // ============================================================================

    const getRange = () => Number(store.range ? store.range.value : 200)
    const getWander = () => Number(store.wander ? store.wander.value : 0.4)

    const update = (dt) => {
        const wander = getWander()
        const range = getRange()
        const allNodes = [...store.neurons.map(n => n.node), ...store.receptors.map(r => r.node)]

        // Wander motion + edge bounce
        if (wander > 0) {
            allNodes.forEach(node => {
                node.wanderTheta += (Math.random() * 2 - 1) * node.wanderStep
                if (node.x < node.radius || node.y < node.radius ||
                    node.x > W - node.radius || node.y > H - node.radius) {
                    node.wanderTheta = Math.atan2(H / 2 - node.y, W / 2 - node.x)
                }
                node.x += Math.cos(node.wanderTheta) * node.wanderSpeed * wander * dt
                node.y += Math.sin(node.wanderTheta) * node.wanderSpeed * wander * dt
                // Clamp
                node.x = Math.max(node.radius, Math.min(W - node.radius, node.x))
                node.y = Math.max(node.radius, Math.min(H - node.radius, node.y))
                // Decay pulse
                node.pulse = Math.max(0, node.pulse - dt / 300)
            })
        }

        // Synapse firing — distance gated
        store.synapses.forEach(syn => {
            const dx = syn.receptor.node.x - syn.neuron.node.x
            const dy = syn.receptor.node.y - syn.neuron.node.y
            const dist = Math.sqrt(dx * dx + dy * dy)
            if (dist < range) {
                syn.connected = true
                syn.strength = 1 - dist / range
                // Fire probabilistically scaled by strength + tempo
                const fireChance = syn.strength * 0.0015 * dt
                if (Math.random() < fireChance && !store.paused && syn.neuron.periodicWave) {
                    syn.pulseT = 1
                    syn.receptor.node.pulse = 1
                    syn.neuron.node.pulse = 1
                    const panX = (syn.receptor.node.x / W) * 2 - 1
                    scheduleNote(
                        syn.receptor.note,
                        syn.neuron.octave,
                        panX,
                        syn.neuron.periodicWave,
                        syn.neuron.oscType,
                    )
                }
            } else {
                syn.connected = false
                syn.strength = 0
            }
            syn.pulseT = Math.max(0, syn.pulseT - dt / 400)
        })

        if (!store.paused) tickSequencer(dt)
    }

    const draw = () => {
        window.requestAnimationFrame(draw)
        const now = performance.now()
        const dt = store.lastClock ? Math.min(100, now - store.lastClock) : 16
        store.lastClock = now

        // LFO tick (matches kaeps/granulr pattern)
        const _lfolinks = window.lfolinks[window.openedbank] || {}
        for (let i in _lfolinks) {
            const x = Number(_lfolinks[i].input.max)
            const u = Number(_lfolinks[i].input.min)
            const lfoval = (_lfolinks[i].lfo.value() + 50)
            const y = Number(_lfolinks[i].lfo.seed.value)
            const z = ((x / 50) * (y * 1))
            let fixed = 0
            const step = '' + _lfolinks[i].input.step
            if (step.includes('.')) fixed = step.split('.')[1].length
            let val = 0
            if (u < 0) {
                if (lfoval > 50) val = (((x / 50) * (lfoval - 50)) + z)
                else val = (-(Math.abs(u) - (((x / 50) * lfoval) + z)))
            } else {
                val = ((x / 100) * lfoval) + (((x / 100) * y))
            }
            if (!fixed) val = parseInt(val)
            else val = Number(val.toFixed(Number(fixed)))
            _lfolinks[i].input.value = val
            _lfolinks[i].input.dispatchEvent(new Event('change'))
        }

        // Update gains from faders
        out.gain.value = Number(store.outvolume.value)

        // ---- Update simulation ----
        update(dt)

        // ---- Draw neuron field ----
        const colors = getThemeColors()
        fieldCtx.fillStyle = colors[0] || '#000'
        fieldCtx.fillRect(0, 0, W, H)

        // Synapse lines + pulses
        store.synapses.forEach(syn => {
            if (!syn.connected) return
            const n = syn.neuron.node
            const r = syn.receptor.node
            // Connection line — opacity by strength
            fieldCtx.strokeStyle = colors[Math.floor(8 + syn.strength * 7)] || colors[12] || '#888'
            fieldCtx.globalAlpha = 0.15 + syn.strength * 0.5
            fieldCtx.lineWidth = 0.5 + syn.strength * 1.5
            fieldCtx.beginPath()
            fieldCtx.moveTo(n.x, n.y)
            fieldCtx.lineTo(r.x, r.y)
            fieldCtx.stroke()
            fieldCtx.globalAlpha = 1

            // Pulse traveling along the synapse
            if (syn.pulseT > 0) {
                const t = 1 - syn.pulseT
                const px = n.x + (r.x - n.x) * t
                const py = n.y + (r.y - n.y) * t
                fieldCtx.fillStyle = colors[15] || '#fff'
                fieldCtx.beginPath()
                fieldCtx.arc(px, py, 2 + syn.pulseT * 3, 0, Math.PI * 2)
                fieldCtx.fill()
            }
        })

        // Receptors (smaller, note-labeled)
        store.receptors.forEach(r => {
            const n = r.node
            const glow = n.pulse
            fieldCtx.beginPath()
            fieldCtx.arc(n.x, n.y, n.radius + glow * 6, 0, Math.PI * 2)
            fieldCtx.fillStyle = r.node.color
            fieldCtx.globalAlpha = 0.3 + glow * 0.7
            fieldCtx.fill()
            fieldCtx.globalAlpha = 1
            fieldCtx.strokeStyle = colors[15] || '#fff'
            fieldCtx.lineWidth = 1
            fieldCtx.stroke()
            // Label
            fieldCtx.fillStyle = colors[0] || '#000'
            fieldCtx.font = '9px monospace'
            fieldCtx.textAlign = 'center'
            fieldCtx.textBaseline = 'middle'
            fieldCtx.fillText(r.note, n.x, n.y)
        })

        // Neurons (larger, octave-labeled, sample contribution dots)
        store.neurons.forEach(n => {
            const node = n.node
            const glow = node.pulse

            // Sample contribution dots — small colored arcs around the neuron
            // showing which samples contribute to its waveform and at what weight
            if (n.weights && n.weights.length > 0 && n.sampleIdx >= 0) {
                const numDots = n.weights.length
                const dotRadius = node.radius + 8
                for (let i = 0; i < numDots; i++) {
                    const w = n.weights[i]
                    if (w < 0.05) continue
                    const angle = (i / numDots) * Math.PI * 2 - Math.PI / 2
                    const dx = node.x + Math.cos(angle) * dotRadius
                    const dy = node.y + Math.sin(angle) * dotRadius
                    const dotSize = 1.5 + w * 3
                    // Primary sample = bright, secondary = dimmer
                    const alpha = i === n.sampleIdx ? 0.9 : w * 0.6
                    fieldCtx.beginPath()
                    fieldCtx.arc(dx, dy, dotSize, 0, Math.PI * 2)
                    fieldCtx.fillStyle = colors[9 + (i % 7)] || colors[12] || '#fff'
                    fieldCtx.globalAlpha = alpha
                    fieldCtx.fill()
                }
                fieldCtx.globalAlpha = 1
            }

            // Outer ring if sample loaded
            if (n.periodicWave) {
                fieldCtx.beginPath()
                fieldCtx.arc(node.x, node.y, node.radius + 6, 0, Math.PI * 2)
                fieldCtx.strokeStyle = colors[15] || '#fff'
                fieldCtx.globalAlpha = 0.4 + glow * 0.6
                fieldCtx.lineWidth = 1.5
                fieldCtx.stroke()
                fieldCtx.globalAlpha = 1
            }
            fieldCtx.beginPath()
            fieldCtx.arc(node.x, node.y, node.radius + glow * 8, 0, Math.PI * 2)
            fieldCtx.fillStyle = node.color
            fieldCtx.globalAlpha = 0.5 + glow * 0.5
            fieldCtx.fill()
            fieldCtx.globalAlpha = 1
            fieldCtx.strokeStyle = colors[15] || '#fff'
            fieldCtx.lineWidth = 1.5
            fieldCtx.stroke()
            // Label
            fieldCtx.fillStyle = colors[0] || '#000'
            fieldCtx.font = '10px monospace'
            fieldCtx.textAlign = 'center'
            fieldCtx.textBaseline = 'middle'
            fieldCtx.fillText('O' + n.octave, node.x, node.y)
            // Drop-target marker — small square inside top-right on all neurons
            // (neurons can receive samples via drag & drop, receptors cannot)
            const sqSize = 7
            const sqX = node.x + node.radius - 8
            const sqY = node.y - node.radius + 2
            fieldCtx.fillStyle = n.periodicWave ? (colors[15] || '#fff') : (colors[9] || '#888')
            fieldCtx.fillRect(sqX, sqY, sqSize, sqSize)
        })

        // ---- Live output waveform + spectrogram (on #vizu) ----
        const ow = outputCanvas.width
        const oh = outputCanvas.height
        outputCtx.fillStyle = colors[0] || '#000'
        outputCtx.fillRect(0, 0, ow, oh)

        // Oscilloscope (left 2/3)
        oscillo.getByteTimeDomainData(oscilloData)
        outputCtx.lineWidth = 1
        outputCtx.strokeStyle = colors[9] || '#ff0'
        outputCtx.beginPath()
        const sliceWidth = (ow * 0.66) * 1.0 / oscilloBufferLen
        let ox = 0
        for (let i = 0; i < oscilloBufferLen; i++) {
            const v = oscilloData[i] / 128.0
            const y = (v * oh) / 2
            if (i === 0) outputCtx.moveTo(ox, y)
            else outputCtx.lineTo(ox, y)
            ox += sliceWidth
        }
        outputCtx.lineTo(ow * 0.66, oh / 2)
        outputCtx.stroke()

        // Spectrogram (right 1/3) — scroll left
        const spearray = new Uint8Array(analyser.frequencyBinCount)
        analyser.getByteFrequencyData(spearray)
        tempCtx.drawImage(specCanvas, 0, 0, 200, 200)
        for (let i = 0; i < spearray.length; i++) {
            const value = spearray[i]
            specCtx.fillStyle = specColor(value)
            specCtx.fillRect(200 - 1, 200 - i, 1, 1)
        }
        specCtx.translate(-1, 0)
        specCtx.drawImage(tempCanvas, 0, 0, 200, 200)
        specCtx.setTransform(1, 0, 0, 1, 0, 0)
        outputCtx.drawImage(specCanvas, ow * 0.66, 0, ow * 0.34, oh)

        // Cadre overlay
        if (cadres && cadres.cadreimg && cadres.cadreimg[0]) {
            outputCtx.drawImage(cadres.cadreimg[0], 0, 0, ow, oh)
        }
    }

    // ============================================================================
    // Interaction — drag neurons/receptors, sample stack management
    // ============================================================================

    let dragging = null // { target, offsetX, offsetY }

    const hitTest = (x, y) => {
        // Neurons first (drawn on top)
        for (let i = store.neurons.length - 1; i >= 0; i--) {
            const n = store.neurons[i].node
            const dx = x - n.x, dy = y - n.y
            if (dx * dx + dy * dy < (n.radius + 4) * (n.radius + 4)) return store.neurons[i]
        }
        for (let i = store.receptors.length - 1; i >= 0; i--) {
            const r = store.receptors[i].node
            const dx = x - r.x, dy = y - r.y
            if (dx * dx + dy * dy < (r.radius + 4) * (r.radius + 4)) return store.receptors[i]
        }
        return null
    }

    fieldCanvas.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return
        const rect = fieldCanvas.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        const hit = hitTest(x, y)
        if (hit) {
            dragging = { target: hit, offsetX: x - hit.node.x, offsetY: y - hit.node.y }
            fieldCanvas.style.cursor = 'grabbing'
        }
    })

    fieldCanvas.addEventListener('mousemove', (e) => {
        const rect = fieldCanvas.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        if (dragging) {
            dragging.target.node.x = Math.max(0, Math.min(W, x - dragging.offsetX))
            dragging.target.node.y = Math.max(0, Math.min(H, y - dragging.offsetY))
        } else {
            const hit = hitTest(x, y)
            fieldCanvas.style.cursor = hit ? 'grab' : 'crosshair'
        }
    })

    const stopDrag = () => { dragging = null; fieldCanvas.style.cursor = 'crosshair' }
    fieldCanvas.addEventListener('mouseup', stopDrag)
    fieldCanvas.addEventListener('mouseleave', stopDrag)

    // Right-click on neuron → clear its sample assignment
    fieldCanvas.addEventListener('contextmenu', (e) => {
        e.preventDefault()
        const rect = fieldCanvas.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        const hit = hitTest(x, y)
        if (hit && store.neurons.includes(hit)) {
            hit.sampleIdx = -1
            hit.sampleName = null
            computeNeuronWave(hit)
            updateStackHighlights()
        }
    })

    // ============================================================================
    // Wavetable mixing — each neuron's waveform is a blend of ALL samples
    // ============================================================================
    // The neuron's "primary" sample (sampleIdx) gets the highest weight.
    // Other samples bleed in with Gaussian falloff controlled by the blend fader.
    // More samples in the stack → richer composite waveforms for every neuron.

    const getBlend = () => Number(store.blend ? store.blend.value : 0.5)

    // Compute Gaussian weights for a neuron across all stack samples
    const computeNeuronWeights = (neuron) => {
        const numSamples = store.sampleStack.length
        if (numSamples === 0) return []
        const blend = getBlend()
        const weights = new Array(numSamples).fill(0)

        if (neuron.sampleIdx < 0 || neuron.sampleIdx >= numSamples) {
            // No primary sample — distribute evenly at low level
            const base = blend * 0.3
            for (let i = 0; i < numSamples; i++) weights[i] = base
            return weights
        }

        // Gaussian centered on the neuron's primary sample
        // sigma scales with blend: 0 = only primary, 1 = wide mix
        const sigma = Math.max(0.5, blend * numSamples * 0.8)
        const center = neuron.sampleIdx
        for (let i = 0; i < numSamples; i++) {
            const dist = Math.abs(i - center)
            // Wrap distance for circular mixing (samples at ends of stack are close)
            const wrapDist = Math.min(dist, numSamples - dist)
            weights[i] = Math.exp(-(wrapDist * wrapDist) / (2 * sigma * sigma))
        }
        // Normalize so primary is always 1.0
        const primaryWeight = weights[center]
        if (primaryWeight > 0) {
            for (let i = 0; i < numSamples; i++) weights[i] /= primaryWeight
        }
        return weights
    }

    // Recompute a single neuron's PeriodicWave from the mixed harmonics
    const computeNeuronWave = (neuron) => {
        if (store.sampleStack.length === 0 || neuron.sampleIdx < 0) {
            neuron.periodicWave = null
            neuron.weights = []
            return
        }
        const weights = computeNeuronWeights(neuron)
        const harmonicsList = store.sampleStack.map(s => s.harmonics)
        neuron.weights = weights
        neuron.periodicWave = mixHarmonicsToWave(store.audioContext, harmonicsList, weights)
    }

    // Recompute ALL neurons' wavetables (call when samples change or blend changes)
    const recomputeAllNeuronWaves = () => {
        store.neurons.forEach(computeNeuronWave)
    }

    // Blend fader changes → recompute all waves
    if (store.blend) {
        store.blend.addEventListener('input', () => recomputeAllNeuronWaves())
    }

    // ============================================================================
    // Sample stack — drop samples here, neurons pick from the stack
    // ============================================================================
    const stackEl = qs('#samplestack')

    // Add a decoded sample to the stack. Returns the stack entry.
    const addSampleToStack = (name, arrayBuffer) => {
        store.audioContext.decodeAudioData(arrayBuffer, (buf) => {
            const harmonics = bufferToHarmonics(buf)
            if (!harmonics) {
                if (alertmessage) alertmessage('could not extract a wavetable cycle from "' + name + '" — skipped')
                return
            }
            const entry = {
                id: uuid(),
                name: name,
                buffer: buf,
                harmonics: harmonics,
                element: null,
            }
            store.sampleStack.push(entry)
            renderStack()
            // Auto-assign to the first neuron without a sample
            const unassigned = store.neurons.find(n => n.sampleIdx < 0)
            if (unassigned) assignSampleToNeuron(store.sampleStack.length - 1, unassigned)
            // Recompute all neurons — new sample enriches everyone's mix
            recomputeAllNeuronWaves()
            if (helpEl) helpEl.style.display = 'none'
        }, () => {
            if (alertmessage) alertmessage('failed to decode "' + name + '"')
        })
    }

    // Assign a stack sample as a neuron's primary sample
    const assignSampleToNeuron = (stackIdx, neuron) => {
        const entry = store.sampleStack[stackIdx]
        if (!entry) return
        neuron.sampleName = entry.name
        neuron.sampleIdx = stackIdx
        computeNeuronWave(neuron)
        updateStackHighlights()
    }

    // Update which stack items are highlighted as assigned
    const updateStackHighlights = () => {
        store.sampleStack.forEach((entry, idx) => {
            const assigned = store.neurons.some(n => n.sampleIdx === idx)
            if (entry.element) {
                entry.element.classList.toggle('assigned', assigned)
            }
        })
    }

    const renderStack = () => {
        if (!stackEl) return
        stackEl.innerHTML = ''
        if (store.sampleStack.length === 0) {
            const empty = build('div')
            empty.className = 'synaps-stack-empty'
            empty.textContent = 'drop samples here'
            stackEl.appendChild(empty)
            return
        }
        store.sampleStack.forEach((entry, idx) => {
            const item = build('div')
            item.className = 'synaps-stack-item'
            item.draggable = true
            item.dataset.stackIdx = idx

            const nameEl = build('div')
            nameEl.className = 'synaps-stack-item-name'
            nameEl.textContent = entry.name
            nameEl.title = entry.name
            item.appendChild(nameEl)

            const removeEl = build('div')
            removeEl.className = 'synaps-stack-item-remove'
            removeEl.textContent = '\u00d7'
            removeEl.title = 'remove'
            item.appendChild(removeEl)

            // Click → assign to first unassigned neuron (or cycle)
            item.addEventListener('click', (e) => {
                if (e.target === removeEl) return
                // Find next unassigned neuron, or cycle to the one with the oldest assignment
                let target = store.neurons.find(n => n.sampleIdx < 0)
                if (!target) {
                    // All have samples — replace the one with the lowest sampleIdx
                    target = store.neurons.reduce((a, b) => (a.sampleIdx < b.sampleIdx ? a : b))
                }
                assignSampleToNeuron(idx, target)
            })

            // Remove button
            removeEl.addEventListener('click', (e) => {
                e.stopPropagation()
                removeSampleFromStack(idx)
            })

            // Drag from stack onto a neuron in the field
            item.addEventListener('dragstart', (e) => {
                e.dataTransfer.setData('application/x-synaps-stack-idx', String(idx))
                e.dataTransfer.effectAllowed = 'copy'
            })

            entry.element = item
            stackEl.appendChild(item)
        })
        updateStackHighlights()
    }

    const removeSampleFromStack = (idx) => {
        // Unassign any neurons using this sample
        store.neurons.forEach(n => {
            if (n.sampleIdx === idx) {
                n.sampleIdx = -1
                n.sampleName = null
            } else if (n.sampleIdx > idx) {
                n.sampleIdx-- // shift down
            }
        })
        store.sampleStack.splice(idx, 1)
        renderStack()
        // Recompute all neurons — mix changes when a sample is removed
        recomputeAllNeuronWaves()
    }

    // ---- Drop samples onto the stack (bucket items + native files) ----
    ['dragenter', 'dragover'].forEach(evt => {
        stackEl.addEventListener(evt, (e) => {
            e.preventDefault()
            if (e.dataTransfer.types.includes('application/x-bucket-item-id') ||
                e.dataTransfer.types.includes('Files')) {
                e.dataTransfer.dropEffect = 'copy'
                stackEl.classList.add('dragover')
            }
        })
    })
    stackEl.addEventListener('dragleave', (e) => {
        if (e.target === stackEl) stackEl.classList.remove('dragover')
    })
    stackEl.addEventListener('drop', async (e) => {
        e.preventDefault()
        stackEl.classList.remove('dragover')

        // Bucket item drop
        const itemId = e.dataTransfer.getData('application/x-bucket-item-id')
        if (itemId && window._synapsBucket && window._synapsBucket.bucket) {
            try {
                const dbItems = await window._synapsBucket.dbGetAll(window._synapsBucket.bucket.bucketId)
                const item = dbItems.find(i => i.id === itemId)
                if (item && item.wav) {
                    addSampleToStack(item.name || 'sample', item.wav.slice(0))
                }
            } catch (err) {
                console.error('[synaps] bucket drop to stack failed', err)
            }
            return
        }

        // Native file drop
        const files = e.dataTransfer.files
        if (files && files.length > 0) {
            for (let i = 0; i < files.length; i++) {
                const file = files[i]
                const reader = new FileReader()
                reader.onload = ((name) => (ev) => addSampleToStack(name, ev.target.result))(file.name)
                reader.readAsArrayBuffer(file)
            }
        }
    })

    // ---- Drop stack items onto neurons in the field ----
    fieldCanvas.addEventListener('dragover', (e) => {
        e.preventDefault()
        if (e.dataTransfer.types.includes('application/x-synaps-stack-idx')) {
            e.dataTransfer.dropEffect = 'copy'
            const rect = fieldCanvas.getBoundingClientRect()
            const x = e.clientX - rect.left
            const y = e.clientY - rect.top
            const hit = hitTest(x, y)
            if (hit && store.neurons.includes(hit)) {
                fieldCanvas.style.outline = '2px dashed var(--cf)'
            } else {
                fieldCanvas.style.outline = '2px dashed var(--c9)'
            }
        }
    })

    fieldCanvas.addEventListener('dragleave', () => {
        fieldCanvas.style.outline = ''
    })

    fieldCanvas.addEventListener('drop', (e) => {
        e.preventDefault()
        fieldCanvas.style.outline = ''
        const stackIdxStr = e.dataTransfer.getData('application/x-synaps-stack-idx')
        if (stackIdxStr === '') return
        const stackIdx = parseInt(stackIdxStr)
        if (isNaN(stackIdx) || !store.sampleStack[stackIdx]) return
        const rect = fieldCanvas.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        const hit = hitTest(x, y)
        if (hit && store.neurons.includes(hit)) {
            assignSampleToNeuron(stackIdx, hit)
        }
    })

    // ============================================================================
    // Bucket integration
    // ============================================================================
    const initLegacyBucket = require('./legacy-bucket-integration.js')
    let _synapsBucket = null
    try {
        _synapsBucket = initLegacyBucket({
            appName: 'synaps',
            bucketContentSelector: '.bucketcontent',
            bucketPanelSelector: '.xtrc-bucket-panel',
            onItemDrop: null, // handled by sample stack drop
        })
    } catch (e) {
        console.error('[synaps] bucket init failed', e)
    }
    window._synapsBucket = _synapsBucket

    // ============================================================================
    // Record button → Recorder → bucket (matches granulr/kaeps pattern)
    // ============================================================================
    const recorder = Recorder(out)
    let isrecording = false
    let rec_i = 0

    function createDownloadLink() {
        recorder.exportWAV(function (blob) {
            const url = URL.createObjectURL(blob)
            const hf = build('a')
            hf.classList.add('hidden')
            hf.href = url
            hf.download = 'SYNAPS_REC_' + rec_i + '.wav'
            rec_i++
            qs('body').appendChild(hf)
            hf.click()
        })
    }

    const recBtn = qs('#record')
    // Record button listener — always attached, independent of bucket state
    recBtn.addEventListener('click', () => {
        isrecording = !isrecording
        recBtn.classList.toggle('active')
        if (isrecording) {
            recorder.record()
            recBtn.style.background = 'var(--c8)'
            recBtn.style.color = 'var(--c0)'
        } else {
            recorder.stop()
            createDownloadLink()
            recorder.clear()
            recBtn.style.background = ''
            recBtn.style.color = ''
        }
    })

    // Bucket interception — separate, only if bucket is available
    if (recBtn && _synapsBucket && _synapsBucket.bucket) {
        recBtn.addEventListener('click', () => {
            // Capture phase fires BEFORE the toggle handler above.
            // So 'active' is present when we're about to STOP recording.
            if (recBtn.classList.contains('active')) {
                const observer = new MutationObserver((mutations) => {
                    for (const mut of mutations) {
                        for (const node of mut.addedNodes) {
                            if (node.tagName === 'A' && node.download && node.download.startsWith('SYNAPS_REC')) {
                                observer.disconnect()
                                fetch(node.href)
                                    .then(r => r.blob())
                                    .then(blob => blob.arrayBuffer())
                                    .then(data => {
                                        _synapsBucket.bucket.add({
                                            source: 'synaps',
                                            name: node.download,
                                            wavLike: data,
                                            fileType: 'wav'
                                        }).catch(() => {})
                                    }).catch(() => {})
                                node.remove()
                            }
                        }
                    }
                })
                observer.observe(document.body, { childList: true, subtree: false })
                setTimeout(() => observer.disconnect(), 5000)
            }
        }, true)
    }

    // ============================================================================
    // Buttons
    // ============================================================================
    qs('#randomize').addEventListener('click', () => {
        randomize()
    })

    qs('#clearsamples').addEventListener('click', () => {
        store.neurons.forEach(n => {
            n.sampleIdx = -1
            n.sampleName = null
        })
        recomputeAllNeuronWaves()
        updateStackHighlights()
    })

    qs('#pause').addEventListener('click', (e) => {
        store.paused = !store.paused
        e.target.classList.toggle('active')
        e.target.innerHTML = store.paused ? '\ue800' : '\ue801'
    })

    // Neuron count fader → rebuild field
    if (store.neuroncount) {
        store.neuroncount.addEventListener('change', () => {
            const oldNeurons = store.neurons
            createNeurons()
            // Preserve sample assignments where possible (match by index)
            oldNeurons.forEach((old, i) => {
                if (store.neurons[i] && old.sampleIdx >= 0) {
                    store.neurons[i].sampleIdx = old.sampleIdx
                    store.neurons[i].sampleName = old.sampleName
                    store.neurons[i].oscType = old.oscType
                }
            })
            layoutField()
            rebuildSynapses()
            recomputeAllNeuronWaves()
            updateStackHighlights()
        })
    }

    // ---- FX list show/hide (matches kaeps/amenpi pattern) ----
    document.addEventListener('click', (e) => {
        const fxlistbox = qs('#fxlist')
        const fxtitle = qs('#fxracktitle')
        if (!fxlistbox || !fxtitle) return
        let target = e.target
        let found = false
        if (target == fxlistbox || target == fxtitle) found = true
        else {
            while (target.parentNode) {
                if (target == fxlistbox || target == fxtitle) { found = true; break }
                target = target.parentNode
            }
        }
        if (!found) fxlistbox.classList.add('hidden')
    })
    qs('#fxracktitle').addEventListener('click', (e) => {
        e.preventDefault()
        qs('#fxlist').classList.remove('hidden')
    })

    // ---- LFO add button ----
    qs('#addlfo').addEventListener('click', () => {
        lfo.createlfo()
    })
    document.body.addEventListener('lfolinked', () => {})
    document.body.addEventListener('unlinkfader', () => {})

    // ============================================================================
    // Boot
    // ============================================================================
    window.addEventListener('resize', resizeCanvases)
    // ResizeObserver catches layout changes (sidebar toggle, etc.) that window resize misses
    if (window.ResizeObserver) {
        const ro = new ResizeObserver(() => resizeCanvases())
        ro.observe(fieldHolder)
        ro.observe(outputCanvas)
    }
    resizeCanvases()
    randomize()
    draw()

    // Resume audio context on first user interaction
    const resumeAudio = () => {
        if (store.audioContext.state === 'suspended') store.audioContext.resume()
    }
    document.addEventListener('click', resumeAudio, { once: true })
    document.addEventListener('keydown', resumeAudio, { once: true })

    // Keyboard shortcuts
    document.addEventListener('keydown', (event) => {
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return
        if (event.code === 'KeyR') qs('#randomize').click()
        if (event.code === 'KeyT') qs('#taptempo').click()
        if (event.code === 'Space') { event.preventDefault(); qs('#pause').click() }
    })
}

// Defer init to allow DOM + scripts to settle (matches kaeps/granulr pattern)
setTimeout(() => { init() }, 200)
